import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@material-ui/core';
import { TEXT, SPACE } from '../../shared_components';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { createStageTemplateApi, updateStageTemplateApi } from '../apiService';

const errorMessage = (response) => response?.data?.message || 'Unable to save the template due to a technical error.';

const StageTemplateDialog = ({ enqueueSnackbar, addEditTemplate, onToggleModal, getResponseBack }) => {
  const isEdit = addEditTemplate?.mode === 'edit';
  const [templateData, setTemplateData] = useState(addEditTemplate?.data || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState({});

  const handleSave = () => {
    const validationInputs = {
      name: templateData?.name?.trim() ? '' : 'Please enter Template Name',
    };
    if (!Object.keys(validationInputs).every((key) => validationInputs[key] === '')) {
      setError(validationInputs);
      trackActivity('Project Management', {
        event_type: 'Add/Update Stage Template Failed',
        page_title: 'Planner Tracker Add/Update Stage Template',
        template_id: templateData?.id || '',
        error_source: 'Frontend',
        error: validationInputs,
      });
      return;
    }

    setSaving(true);
    const payload = { name: templateData?.name?.trim(), description: templateData?.description?.trim() || '' };
    const request = isEdit
      ? globalPutService(updateStageTemplateApi(templateData?.id), payload)
      : globalPostService(createStageTemplateApi(), payload);

    request.then((response) => {
      setSaving(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(isEdit ? 'Template updated' : 'Template created', { variant: 'success' });
        getResponseBack();
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Stage Template Success`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Stage Template`,
          template_id: templateData?.id || '',
          request_body: payload,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Stage Template Failed`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Stage Template`,
          template_id: templateData?.id || '',
          request_body: payload,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <Dialog open={!!addEditTemplate?.modal} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{isEdit ? 'Edit Stage Config Template' : 'New Stage Config Template'}</Typography>
        {!isEdit ? (
          <Typography variant="body2" style={{ color: TEXT.secondary, marginTop: 2 }}>
            A named, self-contained workflow — its own stages, transitions and guards.
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          <TextField
            label="Template Name"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            value={templateData?.name || ''}
            onChange={(event) => setTemplateData({ ...templateData, name: event.target.value.trimStart() })}
            onFocus={() => setError((prev) => (prev.name ? { ...prev, name: '' } : prev))}
            required
            autoFocus
            fullWidth
            error={!!error.name}
            helperText={error.name || ''}
          />
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 500 }}
            value={templateData?.description || ''}
            onChange={(event) => setTemplateData({ ...templateData, description: event.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={() => onToggleModal()}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StageTemplateDialog;
